import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuItem } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { MenuModule } from '@openng/optimus-ui/menu';
import { Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { AppRoutes } from '@reza-ais-fe/reza-ais-fe-utils';
import { WrapperComponent } from '@shared/ui/components';
import { AuthService, DarkModeService, UIService } from '@shared/utils';
import {
  CONTENT_FIXED_WIDTH,
  ContentPageType,
  ContentWidthOption,
  ContentWidthService,
} from '../../services/content-width.service';
import { MobileNavService } from '../../services/mobile-nav.service';

/**
 * Záhlaví aplikace podle gov.cz aplikací (vzor AISK): logo DNI var. A (lev JVS a název produktu, DIAREZA-5905),
 * odkaz Dokumentace, přepínač světlý / tmavý režim, tlačítko uživatele s menu a tlačítko hlavního menu pro úzké
 * obrazovky.
 * Navíc skrytý panel „Šířka obsahu“ (easter egg, ContentWidthService).
 */
@Component({
  selector: 'reza-header',
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    RouterLink,
    RouterLinkActive,
    FormsModule,
    MenuModule,
    PopoverModule,
    ToggleSwitchModule,
    TooltipModule,
    WrapperComponent,
  ],
  // záhlaví podle gov.cz aplikací (vzor AISK): bílý pruh s linkou, v testovacím prostředí výstražné pozadí;
  // v tmavém režimu tmavě žluté, světlé texty záhlaví by na světle žluté nebyly čitelné
  host: {
    class:
      'block text-(--text-primary) bg-(--component-nav-background) border-b border-(--component-nav-separator) [&.is-test]:bg-(--background-status-warning) dark:[&.is-test]:bg-(--color-warning-900)',
    '[class.is-test]': 'isTest',
  },
})
export class HeaderComponent {
  private readonly uiService = inject(UIService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly mobileNav = inject(MobileNavService);
  protected readonly darkMode = inject(DarkModeService);
  protected readonly contentWidth = inject(ContentWidthService);
  protected readonly contentFixedWidth = CONTENT_FIXED_WIDTH;
  /** Panel je mimo řádek záhlaví (viz šablona), tlačítko ho proto otevírá přes viewChild. */
  protected readonly widthPanel = viewChild<Popover>('widthPopover');

  /** Volby panelu „Šířka obsahu“. */
  protected readonly widthOptions: { id: ContentWidthOption; label: string }[] = [
    { id: 'fixedWidth', label: 'Pevná šířka 1440 px na střed' },
    { id: 'fullWidthBrowse', label: 'Přehledy přes celou šířku' },
    { id: 'fullWidthDetail', label: 'Detaily přes celou šířku' },
    { id: 'filterCapped', label: 'Filtr přehledu jako při 1440 px (pole nepřibývají)' },
  ];
  protected readonly pageType = signal<ContentPageType>('jina');
  protected readonly pageTypeLabel: Record<ContentPageType, string> = {
    prehled: 'přehled',
    detail: 'detail',
    jina: 'jiná, výchozí šířka',
  };

  protected hideWidthPanel(): void {
    this.widthPanel()?.hide();
    this.contentWidth.hide();
  }

  readonly isTest = this.uiService.isTestEnv;
  readonly user = toSignal(this.authService.user$, { initialValue: null });
  readonly userMenuOpen = signal(false);
  readonly dokumentaceLink = ['/', AppRoutes.Dms];

  /**
   * Tlačítko uživatele (plné primární, gov-button solid primary, dvouřádkový popisek) se stavy. V šabloně je dvakrát
   * (přihlášený a nepřihlášený uživatel), proto jsou utility tady jednou a šablona je váže přes [class];
   * rozvržení (flex, mezery) je přímo v šabloně.
   */
  protected readonly userButtonClass =
    'min-h-(--height-component-m) max-w-64 py-0.5 px-3 border-none rounded-(--corner-radius-s) text-(--text-white-fixed) bg-(--button-solid-primary) text-left cursor-pointer [transition:background-color_150ms_linear] hover:bg-(--button-solid-primary-hover) active:bg-(--button-solid-primary-active) focus-visible:outline-2 focus-visible:outline-(--status-focus) focus-visible:outline-offset-3';

  /** Položky menu uživatele; třída `logout-link` je kontrakt pro e2e testy (apps/reza-ais-playwright). */
  readonly userMenuItems: MenuItem[] = [
    { label: 'Odhlásit se', icon: 'pi pi-sign-out', styleClass: 'logout-link', command: () => this.logout() },
  ];

  constructor() {
    this.authService.checkAuthAndLoadUser();
  }

  login(): void {
    this.router.navigate(['/login']);
  }

  logout(): void {
    this.authService.logout();
  }

  toggleMobileNav(event: MouseEvent): void {
    // bez stopPropagation by click doběhl na document a navigace by menu hned zavřela (outside click)
    event.stopPropagation();
    this.mobileNav.toggle$.next(event);
  }
}
